export function compactAlphanumeric(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function countVowels(value: string): number {
  // y counts so Flynn/Lynn/Smyth are not treated as tpcc-style acronyms.
  return (value.match(/[aeiouy]/g) ?? []).length;
}

// 3-letter first-word stem + remaining facility initials (trapc → tra+pc).
// Names like lloyd/ralph/blanc do not end in these tails.
const FACILITY_INITIAL_TAILS = new Set(["pc", "cc", "gc", "bc", "lc", "pcc", "gcc", "bcc", "pgc", "cgc", "bgc", "lcc"]);

export function isHybridFacilityAcronymQuery(compact: string): boolean {
  if (!/^[a-z]{5,6}$/.test(compact)) return false;
  if (countVowels(compact) !== 1) return false;
  const stem = compact.slice(0, 3);
  const rest = compact.slice(3);
  if (countVowels(stem) !== 1 || countVowels(rest) !== 0) return false;
  return FACILITY_INITIAL_TAILS.has(rest);
}

export function isStrongFacilityIntentQuery(query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return false;
  if (/\s/.test(trimmed)) return true;
  const compact = compactAlphanumeric(trimmed);
  if (!compact) return false;
  if (compact.length >= 3 && compact.length <= 6 && countVowels(compact) === 0) return true;
  return isHybridFacilityAcronymQuery(compact);
}

export function isConcatenatedFacilityQuery(name: string, query: string): boolean {
  const compactQuery = compactAlphanumeric(query.trim());
  const compactName = compactAlphanumeric(name);
  if (compactQuery.length < 8 || !compactName.includes(compactQuery)) return false;
  const first = facilityWords(name)[0] ?? "";
  return compactQuery !== first && compactQuery.length > first.length;
}

function facilityWords(name: string): string[] {
  const stop = new Set(["the", "of", "at", "and", "a"]);
  return name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word && !stop.has(word));
}

function initialsOf(words: string[]): string {
  return words.map((word) => word[0] ?? "").join("");
}

function tokenMatchesFacility(token: string, words: string[], initials: string): boolean {
  const compactToken = compactAlphanumeric(token);
  if (!compactToken) return false;
  if (words.some((word) => word.startsWith(compactToken))) return true;
  if (compactToken.length >= 2 && initials.includes(compactToken)) return true;
  return false;
}

export function facilityMatchesQuery(name: string, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed || !name.trim()) return false;

  const compactName = compactAlphanumeric(name);
  const compactQuery = compactAlphanumeric(trimmed);
  if (compactQuery.length >= 5 && compactName.includes(compactQuery)) return true;
  if (compactName.length >= 5 && compactQuery.includes(compactName)) return true;

  const words = facilityWords(name);
  if (words.length === 0) return false;
  const initials = initialsOf(words);
  if (compactQuery.length >= 2 && (compactQuery === initials || initials.startsWith(compactQuery))) return true;

  const first = words[0] ?? "";
  for (const prefixLen of [2, 3]) {
    if (first.length <= prefixLen) continue;
    const withAll = `${first.slice(0, prefixLen)}${initialsOf(words.slice(1))}`;
    if (compactQuery === withAll) return true;
    if (words.length > 2) {
      const skipLast = `${first.slice(0, prefixLen)}${initialsOf(words.slice(1, -1))}`;
      if (compactQuery === skipLast) return true;
    }
  }

  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) {
    return tokens.every((token) => tokenMatchesFacility(token, words, initials));
  }

  return words.some((word) => word.startsWith(compactQuery));
}

function concatenatedNameMatches(first: string, last: string, compactQuery: string): boolean {
  if (!compactQuery) return false;
  const forward = `${first}${last}`;
  if (forward.length >= 3 && forward.startsWith(compactQuery)) return true;
  if (first && last) {
    const reversed = `${last}${first}`;
    if (reversed.length >= 3 && reversed.startsWith(compactQuery)) return true;
  }
  return false;
}

function personNameParts(firstName: string, lastName: string): string[] {
  const first = compactAlphanumeric(firstName);
  const lastWords = lastName.split(/\s+/).map(compactAlphanumeric).filter(Boolean);
  return [first, ...lastWords].filter(Boolean);
}

function reverseLastNameTokenOk(token: string, lastWord: string): boolean {
  if (!lastWord.startsWith(token)) return false;
  if (token === lastWord || lastWord.length <= 4) return true;
  return token.length >= 5;
}

const LAST_NAME_SUFFIX_TOKENS = new Set(["jr", "sr", "ii", "iii", "pga"]);

function isSkippedLastNameToken(token: string): boolean {
  return token.length <= 1 || LAST_NAME_SUFFIX_TOKENS.has(token);
}

function lastSignificantLastNamePart(parts: string[]): string {
  for (let i = parts.length - 1; i >= 1; i--) {
    if (!isSkippedLastNameToken(parts[i])) return parts[i];
  }
  return parts[parts.length - 1];
}

export function personNameMatchesQuery(
  user: { firstName: string; lastName: string; email?: string },
  query: string
): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return false;

  const compactQuery = compactAlphanumeric(trimmed);
  const first = compactAlphanumeric(user.firstName);
  const last = compactAlphanumeric(user.lastName);
  const email = compactAlphanumeric(user.email || "");

  if (trimmed.includes("@") && email && email.includes(compactQuery)) return true;
  if (email && compactQuery.length >= 5 && email.startsWith(compactQuery)) return true;
  if (concatenatedNameMatches(first, last, compactQuery)) return true;

  const tokens = trimmed.split(/\s+/).filter(Boolean).map(compactAlphanumeric).filter(Boolean);
  if (tokens.length >= 2) {
    const parts = personNameParts(user.firstName, user.lastName);
    if (parts.length < 2) return false;
    const head = tokens[0];
    const tail = tokens[tokens.length - 1];
    const firstPart = parts[0];
    const lastPart = lastSignificantLastNamePart(parts);
    if (!tokens.every((token) => parts.some((part) => part.startsWith(token)))) return false;
    const forward = firstPart.startsWith(head) && lastPart.startsWith(tail);
    const reverse = reverseLastNameTokenOk(head, lastPart) && firstPart.startsWith(tail);
    return forward || reverse;
  }

  return first.startsWith(compactQuery) || last.startsWith(compactQuery);
}

export function noteTitleMatchesQuery(title: string, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return false;
  const compactQuery = compactAlphanumeric(trimmed);
  const compactTitle = compactAlphanumeric(title);
  if (compactQuery.length >= 4 && compactTitle.includes(compactQuery)) return true;
  const tokens = trimmed.split(/\s+/).filter((token) => token.length >= 3);
  if (tokens.length === 0) return false;
  const lowerTitle = title.toLowerCase();
  return tokens.every((token) => lowerTitle.includes(token));
}

export function workplaceMatchesQuery(facilityName: string | null | undefined, query: string): boolean {
  return Boolean(facilityName && facilityMatchesQuery(facilityName, query));
}

export type AlgoliaMentionQuery = {
  primaryQuery: string;
  optionalWords?: string[];
  extraQueries: string[];
  facilityPrefixQueries: string[];
  spacedInitialsQuery?: string;
};

function uniqueQueries(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

export function buildAlgoliaMentionQuery(query: string): AlgoliaMentionQuery {
  const trimmed = query.trim();
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  const shortTokens = tokens.filter((token) => token.length <= 2);
  const longTokens = tokens.filter((token) => token.length > 2);
  const compact = compactAlphanumeric(trimmed);
  const extraQueries: string[] = [];
  const facilityPrefixQueries: string[] = [];

  if (tokens.length >= 2 && compact.length >= 4) {
    extraQueries.push(compact);
  }

  if (!trimmed.includes(" ") && compact.length >= 6) {
    extraQueries.push(compact.slice(0, Math.min(6, compact.length - 2)));
  }

  // Acronyms like tpcc are not in the Algolia name field. Spaced letters let
  // Algolia AND word prefixes (t p c c → Travis Pointe Country Club). Last names
  // such as smith/kelly/lloyd/flynn must not take the hybrid or zero-vowel path.
  // trapc uses a 3-letter prefix.
  let spacedInitialsQuery: string | undefined;
  if (!trimmed.includes(" ") && /^[a-z]{3,6}$/.test(compact)) {
    if (countVowels(compact) === 0) {
      spacedInitialsQuery = compact.split("").join(" ");
    } else if (isHybridFacilityAcronymQuery(compact)) {
      facilityPrefixQueries.push(compact.slice(0, 3));
    }
  }

  const extras = uniqueQueries(extraQueries);
  const prefixes = uniqueQueries(facilityPrefixQueries);

  if (longTokens.length > 0) {
    return {
      primaryQuery: longTokens.join(" "),
      ...(shortTokens.length > 0 ? { optionalWords: shortTokens } : {}),
      extraQueries: extras,
      facilityPrefixQueries: prefixes,
      ...(spacedInitialsQuery ? { spacedInitialsQuery } : {})
    };
  }

  return {
    primaryQuery: trimmed,
    extraQueries: extras,
    facilityPrefixQueries: prefixes,
    ...(spacedInitialsQuery ? { spacedInitialsQuery } : {})
  };
}
